import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import AccountsPayableConfirmation, {
  IAccountsPayableConfirmation,
} from '@components/modals/AccountsPayableConfirmation';
import {
  AccountsPayableLinkStatus,
  AccountsPayableLinkUpdateStatus,
} from '@constants/accountsPayableLink';
import useStore from '@hooks/useStore';
import { IModalComponent } from '@yaydoo/react-components/lib/components/ModalWrapper/ModalContext';
import useModal from '@yaydoo/react-components/lib/hooks/useModal';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';
import useStyles from '@containers/AccountsPayable/styles';

export interface IUseAccountsPayable {
  openSendRequestModal: () => void;
  openCancelOperationModal: () => void;
  openConfirmOperationModal: (
    total: number,
    partner: string,
    accountsPayableLinkId: string,
  ) => void;
  handleError: (error: { message: string }) => void;
  showSuccessToast: ({ title, message }: { title: string; message: string }) => void;
}

function useAccountsPayable(): IUseAccountsPayable {
  const { t } = useTranslation();
  const { openToast } = useToast();
  const { showModal } = useModal();
  const { accountsPayableLink } = useStore();
  const history = useHistory();
  const css = useStyles();

  const showSuccessToast = ({ title, message }: { title: string; message: string }) => {
    openToast({
      type: 'success',
      title,
      message,
    });
  };

  const handleError = (error: { message: string }) => {
    const { message = '' } = error;
    openToast({
      type: 'error',
      title: t('accountsPayable.toast.error.title'),
      message: message || t('accountsPayable.toast.error.unknown'),
    });
  };

  const handleUpdateStatus = async (status: AccountsPayableLinkUpdateStatus) => {
    try {
      await accountsPayableLink.updateStatus(status);
      accountsPayableLink.setCurrentStatus(status as unknown as AccountsPayableLinkStatus);
    } catch (error) {
      handleError(error as { message: string });
    }
  };

  const handleCancelAccountsPayable = async (accountsPayableLinkId: string) => {
    try {
      await accountsPayableLink.cancelAccountsPayable();
      await accountsPayableLink.updateStatus(AccountsPayableLinkUpdateStatus.CONFIRMED);
      accountsPayableLink.setCurrentStatus(AccountsPayableLinkStatus.CONFIRMED);
      await accountsPayableLink.getAccountsPayableLink(accountsPayableLinkId);
    } catch (error) {
      handleError(error as { message: string });
    }
  };

  const handleRedirect = () => {
    history.push(`/accounts-payable/${accountsPayableLink.accountsPayableLinkId}`);
  };

  const openSendRequestModal = () => {
    showModal({
      component: AccountsPayableConfirmation,
      props: {
        title: t('accountsPayable.sendRequestModal.title'),
        message: t('accountsPayable.sendRequestModal.description'),
        confirmLabelButton: t('accountsPayable.sendRequestModal.confirmButton'),
        cancelLabelButton: t('accountsPayable.sendRequestModal.cancelButton'),
        cancel: false,
        onConfirm: handleRedirect,
        titleClassName: css.modalConfirmationTitle,
        messageClassName: css.modalConfirmationMessage,
        cancelBtnClass: css.cancelButton,
        submittingBtnClass: css.confirmButton,
        wide: 610,
        isWide: true,
      } as IAccountsPayableConfirmation,
    } as unknown as IModalComponent);
  };

  const openCancelOperationModal = () => {
    showModal({
      component: AccountsPayableConfirmation,
      props: {
        title: t('accountsPayable.cancelModal.title'),
        message: t('accountsPayable.cancelModal.description'),
        subMessage: '¿Deseas continuar?',
        confirmLabelButton: 'Si, cancelar',
        cancelLabelButton: 'Cerrar',
        onConfirm: () => handleUpdateStatus(AccountsPayableLinkUpdateStatus.CANCELED),
        titleClassName: css.modalConfirmationTitle,
        messageClassName: css.modalConfirmationMessage,
        cancelBtnClass: css.cancelButton,
        submittingBtnClass: css.confirmCancelButton,
        wide: 610,
        isWide: true,
      } as IAccountsPayableConfirmation,
    } as unknown as IModalComponent);
  };

  const openConfirmOperationModal = (
    total: number,
    partner: string,
    accountsPayableLinkId: string,
  ) => {
    showModal({
      component: AccountsPayableConfirmation,
      props: {
        title: `No seleccionaste ${total > 1 ? total + ' pagos' : total + ' pago'}`,
        message: `Los pagos que no hayas seleccionado serán omitidos del proceso de dispersión${
          partner === 'netsuite' ? ' y regresarán a su estado original en Netsuite.' : '.'
        }`,
        subMessage: '¿Deseas continuar?',
        confirmLabelButton: 'Si, continuar',
        cancelLabelButton: 'Cerrar',
        onConfirm: () => handleCancelAccountsPayable(accountsPayableLinkId),
        titleClassName: css.modalConfirmationTitle,
        messageClassName: css.modalConfirmationMessage,
        cancelBtnClass: css.cancelButton,
        submittingBtnClass: css.confirmButton,
        wide: 610,
        isWide: true,
      } as IAccountsPayableConfirmation,
    } as unknown as IModalComponent);
  };

  return {
    openSendRequestModal,
    openCancelOperationModal,
    openConfirmOperationModal,
    handleError,
    showSuccessToast,
  };
}
export default useAccountsPayable;
